import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { CartContainer, EntityList } from '@aimsos/ui';

import { DRIVE_STATUS_VARIANTS } from '../../../constants/knowledge.js';
import { formatDateWithYear } from '../../../utils/traitDates.js';

import {
  AudioLines,
  CircleSlash,
  Clock,
  File,
  FileSpreadsheet,
  Files,
  FileText,
  Folder,
  Info,
  Link2,
  RefreshCw,
  Sparkles,
  User,
} from 'lucide-react';

/** "64 KB", "1.2 MB" — a file's size. */
const formatSize = (bytes) => {
  if (bytes == null) return null;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, '')} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

/** "Today", or "Oct 8, 2026" — the prototype always writes the year. */
const formatDate = (iso, language, t, now = new Date()) => {
  if (iso && new Date(iso).toDateString() === now.toDateString()) return t('knowledge.drive.item.today');
  return formatDateWithYear(iso, language);
};

const NEUTRAL = { bgColor: 'Neutral', iconColor: 'Neutral' };
const PURPLE = { bgColor: 'Purple', iconColor: 'Purple' };

/** The icon of a file, as in the prototype: recordings, live docs, sheets, the rest. */
const fileIcon = (item) => {
  if (item.status.includes('recording')) return { icon: AudioLines, iconHighlight: PURPLE };
  if (item.status.includes('live')) return { icon: RefreshCw, iconHighlight: NEUTRAL };
  if (item.fileType === 'sheet') return { icon: FileSpreadsheet, iconHighlight: NEUTRAL };
  return { icon: FileText, iconHighlight: NEUTRAL };
};

/**
 * One folder or file of the record's drive (CT-5717) — `CartContainer` +
 * `EntityList`, the domain's row pair, laid out as in the prototype:
 *
 *   folder   name and "N files"; where it came from and "Read · N claims"; Open
 *   file     its icon (recording, live doc, sheet, document); name, who created
 *            it, where it came from and its size; the date (with its year) and
 *            "N claims extracted" or "Not read for claims"; "Meeting record" and
 *            View claims on the right; the status chips
 */
const DriveItemCard = memo(function DriveItemCard({ item, onOpenFolder, onViewClaims }) {
  const { t, i18n } = useTranslation('unified-profiles');
  const language = i18n?.language;

  const handleOpen = useCallback(() => onOpenFolder(item.id), [onOpenFolder, item.id]);
  const handleClaims = useCallback(() => onViewClaims(item.id), [onViewClaims, item.id]);

  const view = useMemo(() => {
    if (item.kind === 'folder') {
      return {
        title: item.name,
        icon: Folder,
        iconHighlight: NEUTRAL,
        primaryMeta: [{ id: 'files', icon: Files, label: t('knowledge.drive.item.files', { count: item.fileCount ?? 0 }) }],
        secondaryMeta: [
          item.sourceLine ? { id: 'source', icon: Info, label: item.sourceLine } : null,
          { id: 'read', icon: Sparkles, label: t('knowledge.drive.item.folderRead', { count: item.claimsExtracted }) },
        ].filter(Boolean),
        secondaryCta: { label: t('knowledge.drive.item.open'), onClick: handleOpen },
      };
    }

    const size = formatSize(item.sizeBytes);
    const date = formatDate(item.occurredAt, language, t);
    let reading = null;
    if (!item.read) reading = { id: 'reading', icon: CircleSlash, label: t('knowledge.drive.item.notRead') };
    else if (item.claimsExtracted) {
      reading = { id: 'reading', icon: Sparkles, label: t('knowledge.drive.item.claims', { count: item.claimsExtracted }) };
    }

    return {
      title: item.name,
      ...fileIcon(item),
      primaryMeta: [
        item.createdBy ? { id: 'creator', icon: User, label: t('knowledge.drive.item.createdBy', { name: item.createdBy }) } : null,
        item.sourceLine ? { id: 'source', icon: Link2, label: item.sourceLine } : null,
        size ? { id: 'size', icon: File, label: size } : null,
      ].filter(Boolean),
      // "Meeting record" names what the file is, beside View claims — not one more chip.
      timestamp: item.status.includes('meeting_record') ? (
        <span data-testid="drive-item-record">{t('knowledge.drive.status.meeting_record')}</span>
      ) : undefined,
      secondaryMeta: [date ? { id: 'date', icon: Clock, label: date } : null, reading].filter(Boolean),
      tags: item.status
        .filter((status) => status !== 'meeting_record')
        .map((status) => ({ label: t(`knowledge.drive.status.${status}`), variant: DRIVE_STATUS_VARIANTS[status] ?? 'neutral' })),
      secondaryCta:
        item.claimsExtracted && onViewClaims ? { label: t('knowledge.drive.item.viewClaims'), onClick: handleClaims } : undefined,
    };
  }, [item, language, handleOpen, handleClaims, onViewClaims, t]);

  return (
    <div data-testid="drive-item" data-item-id={item.id} data-kind={item.kind}>
      <CartContainer size="S" variant="neutral">
        <EntityList {...view} />
      </CartContainer>
    </div>
  );
});

export default DriveItemCard;
